import React, { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Button, Grid, IconButton, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import Loader from '../../components/Loader';
import ErrorScreen from '../../components/ErrorScreen';
import SearchField from '../../components/input/SearchField';
import Layout from '../../components/Layout/Layout';
import ModalCustom from '../../components/modals/ModalCustom';
import AddNew from './AddNew';
import Edit from './Edit';
import ModalConfirmation from '../../components/modals/ModalConfirmation';
import { apiRequest } from '../../utils/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function Goods() {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [loadingDelReq, setLoadingDelReq] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [good, setGood] = useState(null);
    const [goods, setGoods] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    const fetchGoods = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await apiRequest('get', 'goods');
            setGoods(res?.data || []);
            setFilteredData(res?.data || []);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        fetchGoods();
    }, []);

    const handleSearch = (searchText) => {
        const filteredResults = goods.filter((row) => row?.name?.toLowerCase().includes(searchText?.toLowerCase()));
        setFilteredData(filteredResults);
    };

    const handleDelete = async () => {
        setLoadingDelReq(true);
        try {
            const res = await apiRequest('delete', `goods/${good?.id}`, {});

            if (res?.data) {
                setGood(null);
                fetchGoods();
                setShowDeleteModal(false);
                return toast.success('Deleted Successfully!');
            }
        } catch (error) {
            toast.error(error?.response?.data?.message ?? error?.response?.data ?? error?.message);
        } finally {
            setLoadingDelReq(false);
        }
    };

    return (
        <Layout>
            <>
                <Grid container sx={{ mb: 2 }} spacing={2} alignItems={'center'}>
                    <Grid item lg={7.5} sm={3.5} md={6.5} xs={6}>
                        <Typography variant="h6">Goods</Typography>
                    </Grid>

                    <Grid item lg={1.5} sm={3.5} md={2.5} xs={6} sx={{ display: { xs: 'block', sm: 'none' } }}>
                        <Button onClick={() => setShowCreateModal(true)} fullWidth variant="contained">
                            Create Good
                        </Button>
                    </Grid>

                    <Grid item lg={3} sm={5} md={3} xs={12}>
                        <SearchField onChange={(e) => handleSearch(e?.target?.value)} label={'Search Goods'} />
                    </Grid>

                    <Grid item lg={1.5} sm={3.5} md={2.5} xs={12} sx={{ display: { xs: 'none', sm: 'block' } }}>
                        <Button onClick={() => setShowCreateModal(true)} fullWidth variant="contained">
                            Create Good
                        </Button>
                    </Grid>
                </Grid>
                {loading && <Loader />}

                {!!filteredData && (
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell sx={{ width: '60px' }} />
                                    <TableCell sx={{ width: '60px' }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData?.map((row) => (
                                    <TableRow key={row.name} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell>{row?.name}</TableCell>
                                        <TableCell>
                                            <IconButton
                                                onClick={() => {
                                                    setGood(row);
                                                    setShowEditModal(true);
                                                }}
                                                aria-label="Edit"
                                                size="small"
                                            >
                                                <BorderColorIcon sx={{ color: '#1976d2' }} />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                onClick={() => {
                                                    setGood(row);
                                                    setShowDeleteModal(true);
                                                }}
                                                aria-label="Delete"
                                                size="small"
                                            >
                                                <DeleteIcon sx={{ color: 'red' }} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {!!error && <ErrorScreen error={error} />}
            </>

            {showCreateModal && (
                <ModalCustom open={showCreateModal} title={'Create Good'}>
                    <AddNew
                        onClose={() => setShowCreateModal(false)}
                        onRefresh={fetchGoods}
                    />
                </ModalCustom>
            )}

            {showEditModal && (
                <ModalCustom open={showEditModal} title={'Edit Good'}>
                    <Edit
                        good={good}
                        onClose={() => setShowEditModal(false)}
                        onRefresh={fetchGoods}
                    />
                </ModalCustom>
            )}

            <ModalConfirmation
                open={showDeleteModal}
                loading={loadingDelReq}
                header={'Delete Good'}
                description={`Are you sure, you want to delete ${good?.name}?`}
                onClickNo={() => setShowDeleteModal(false)}
                onClickYes={handleDelete}
            />
        </Layout>
    );
}
